import { createStore } from "zustand/vanilla";
import { conflict } from "@pack-it-up/shared";
import { errorToMessage } from "../errorToMessage.js";
// Doc 04 §4 send guard: reject self / already-friends / pending-outgoing before
// the network. Returns a reason string when blocked.
const sendBlockedReason = (state, me, toUserId) => {
    if (toUserId === me)
        return "You can't send a friend request to yourself.";
    if (state.friends.some((f) => f.userId === toUserId))
        return "You're already friends.";
    if (state.outgoing.some((o) => o.toUserId === toUserId))
        return "A request is already pending.";
    return null;
};
export const createFriendsStore = (deps) => {
    const me = deps.userId;
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set, get) => ({
        friends: [],
        incoming: [],
        outgoing: [],
        lookupResult: null,
        error: null,
        async lookupByEmail(email) {
            const r = await deps.friends.lookupByExactEmail(email);
            if (r.ok)
                set({ lookupResult: r.value, error: null });
            else
                set({ error: errorToMessage(r.error) });
        },
        async sendRequest(toUserId) {
            const reason = sendBlockedReason(get(), me, toUserId);
            if (reason) {
                set({ error: reason });
                return { ok: false, error: conflict(reason) };
            }
            // optimistic: show it immediately; the authoritative stream replaces it.
            const prevOutgoing = get().outgoing;
            const optimistic = {
                id: `optimistic:${toUserId}`,
                fromUserId: me,
                toUserId,
                status: "pending",
                createdAt: new Date(),
            };
            set({ outgoing: [...prevOutgoing, optimistic], error: null });
            const r = await deps.friends.sendRequest(toUserId);
            if (!r.ok)
                set({ outgoing: prevOutgoing, error: errorToMessage(r.error) });
            return r;
        },
        async acceptRequest(requestId) {
            const r = await deps.friends.acceptRequest(requestId);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async ignoreRequest(requestId) {
            const r = await deps.friends.ignoreRequest(requestId);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async removeFriend(friendUserId) {
            const r = await deps.friends.removeFriend(friendUserId);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        dispose() {
            for (const unsubscribe of subs.splice(0))
                unsubscribe();
        },
    }));
    subs.push(deps.friends.observeFriends(me).subscribe((friends) => store.setState({ friends })), deps.friends.observeIncomingRequests(me).subscribe((incoming) => store.setState({ incoming })), deps.friends.observeOutgoingRequests(me).subscribe((outgoing) => store.setState({ outgoing })));
    return store;
};
//# sourceMappingURL=friendsStore.js.map